/**
 * @component LoginContainer
 * @description Main login container component that handles multiple authentication methods:
 * - Standard username/password authentication
 * - Okta authentication
 * - Keycloak authentication
 *
 * Component Structure:
 * └─ LoginContainer
 *    ├─ StandardLoginForm
 *    │  └─ Slider (Pulic Announcements carousel)
 *    │  └─ LoginForm
 *    │  └─ Footer
 *    ├─ ForgotPassword
 *    ├─ ResetPasswordPage
 *    ├─ oktaLoginRedirect
 *    └─ KcLogin (Keycloak login provider)
 *       └─ ReactKeycloakProvider
 *
 * Features:
 * - Multiple authentication methods (Standard/Okta/Keycloak)
 * - Secure token management
 * - Password recovery flow
 * - Token validation
 * - Responsive design
 *
 * @example
 * // Standard initialization
 * new LoginContainer(core, options);
 *
 * @props {Object} core - OSJS Core instance
 * @props {Object} options - Configuration options
 */

import { Login as defaultLogin } from "../../osjs-client/index.js";
import React from "react";
import ReactDOM from "react-dom/client";
import OktaAuth from "@okta/okta-auth-js";
import { ReactKeycloakProvider } from "@react-keycloak/web";
import Keycloak from "keycloak-js";
// Component imports
import ForgotPassword from "./ForgotPassword.js";
import ResetPasswordPage from "./ResetPasswordPage.js";
import Slider from "./Slider.js";
// Asset imports
import "../assets/scss/login.scss";
import oxLogo from "../assets/images/Taruvi_transparent.png";
import wallpaper from "../assets/images/wallpaper.png";

export default class LoginContainer extends defaultLogin {
  constructor(core, options = {}) {
    super(core, options || {});
    this.core = core;

    this.baseurl = this.core.config("ui.url");
    this.viewurl = this.core.config("wrapper.url");
  }

  checkOxzionGuiAvailability = () => {
    return new Promise((resolve, reject) => {
      let attempts = 0;
      const maxAttempts = 20;

      const check = () => {
        if (window.oxziongui) {
          resolve(true);
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(check, 250);
        } else {
          reject(new Error('oxziongui not available after maximum attempts'));
        }
      };

      check();
    });
  }

  initializeKeycloak() {
    this.keycloakUrl = this.core.config("keycloak.keycloakUrl");
    this.keycloakRealm = this.core.config("keycloak.keycloakRealm");
    this.keycloakClientId = this.core.config("keycloak.keycloakClientId");
    this.keycloak = new Keycloak({
      url: this.keycloakUrl,
      realm: this.keycloakRealm,
      clientId: this.keycloakClientId,
    });
  }

  initializeOkta() {
    const oktaIssuer = this.core.config("okta.oktaIssuer");
    const oktaClientId = this.core.config("okta.oktaClientId");
    const oktaRedirectUri = this.core.config("okta.oktaRedirectUri");

    this.oktaAuth = new OktaAuth({
      issuer: oktaIssuer,
      clientId: oktaClientId,
      redirectUri: oktaRedirectUri,
    });
  }

  validateToken = async (form, type) => {
    return await (
      await fetch(this.viewurl + `auth/external/${type}`, {
        method: "post",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(form),
        credentials: 'include'
      })
    ).json();
  };

  //logins in to okta if already logged in, else redirects to okta to login
  async handleOktaLogin() {
    try {
      const tokens = await this.oktaAuth.token.parseFromUrl();
      this.oktaAuth.tokenManager.setTokens(tokens.tokens);
      const accessToken = tokens.tokens.accessToken.accessToken;
      if (accessToken) {
        const form = new FormData();
        form.append("access_token", accessToken);
        let validate = await this.validateToken(form, "okta");
        if (validate.error) {
          console.error("Error from server:", validate.error);
          return;
        }

        let res = [{ result: validate.data, ref_token: validate.data.refresh_token }];
        this.emit("login:post", res);
      }
    } catch (error) {
      if (error.name === "OAuthError") {
        console.error("Okta OAuth Error:", error);
      } else if (error.name === "AuthApiError") {
        console.error("Okta Auth API Error:", error);
      } else {
        this.oktaLoginRedirect();
      }
    }
  }

  async handleKeycloakLogin(container) {
    const redirect = window.location.href.split(/#state|&code|&session_state/).length === 4;
    if (!redirect) {
      ReactDOM.createRoot(container).render(KcLogin(this.keycloak));
    } else {
      const formData = new FormData();
      formData.append("grant_type", "authorization_code");
      formData.append("client_id", this.keycloakClientId);
      formData.append("redirect_uri", this.baseurl);
      formData.append("code", window.location.href.split("&code=")[1]);

      const response = await (
        await fetch(this.keycloakUrl + "/realms/" + this.keycloakRealm + "/protocol/openid-connect/token", {
          body: new URLSearchParams(formData),
          method: "post",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        })
      ).json();

      if (response.access_token) {
        const keycloakValidateForm = new FormData();
        keycloakValidateForm.append("access_token", response.access_token);
        let keycloakValidateResponse = await this.validateToken(keycloakValidateForm, "keycloak");
        let res = [{ result: keycloakValidateResponse.data, ref_token: response.refresh_token }];
        this.emit("login:post", res);
        window.location.replace(window.location.href.split("#state=")[0]);
      }
    }
  }

  oktaLoginRedirect() {
    try {
      this.oktaAuth.token.getWithRedirect({
        responseType: ["id_token", "token"],
        scopes: ["openid", "profile", "email"],
      });
    } catch (error) {
      console.error("Error initiating Okta login:", error);
    }
  }

  //add another condition - if there is #login in the url params, then, even if okta or keycloak is enabled then we need to redirect to our login page
  //when logging in through okta and refreshing then it is going back to okta login

  async initializeAuthenticationFlow(container) {
    const authType = window.location.href.includes("#login") ? null : this.core.config("authType");
    switch (authType) {
      case "okta":
        this.initializeOkta();
        await this.handleOktaLogin(container);
        break;
      case "keycloak":
        this.initializeKeycloak();
        await this.handleKeycloakLogin(container);
        break;
      default:
        // Handles these two cases:
        // 1. When #login is in URL
        // 2. When no authType is configured (this.core.config("authType") is null/undefined)
        ReactDOM.createRoot(container).render(<StandardLoginForm core={this.core} thisObj={this} />);
        this.on("login:stop", () => {
          let AuthToken = this.core.make("oxzion/profile").getAuth();
          if (AuthToken) {
            ReactDOM.createRoot(container).unmount();
          }
        });
        break;
    }
  }

  async render() {
    try {
      await this.checkOxzionGuiAvailability()
    } catch (error) {
      console.error(error);
    }

    const createLoginContainer = () => {
      const node = document.createElement("div");
      node.className = "reactLoginPage";
      document.body.appendChild(node);
      return node;
    };

    const redirectToLogin = () => window.location.replace(this.baseurl);

    const renderComponent = (Component, props = {}) => {
      const container = createLoginContainer();
      ReactDOM.createRoot(container).render(
        <React.Suspense fallback={<div>Loading...</div>}>
          <Component core={this.core} showLoginPage={redirectToLogin} {...props} />
        </React.Suspense>,
      );
    };

    const routes = {
      "#forgotpassword": () => renderComponent(ForgotPassword),
      resetpassword: () =>
        renderComponent(ResetPasswordPage, {
          resetPasswordToken: new URLSearchParams(window.location.search).get("resetpassword"),
        }),
      default: () => this.initializeAuthenticationFlow(createLoginContainer()),
    };

    // Find matching route or use default
    const currentPath = Object.keys(routes).find((path) => window.location.href.includes(path)) || "default";

    // Execute the matching route handler
    routes[currentPath]();
  }
}

const KcLogin = (keycloak) => {
  const redirect = window.location.href.split(/#state|&code|&session_state/).length === 4;

  return !redirect ? (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={{ onLoad: "login-required", checkLoginIframe: false }} // onLoad: "login-required"
      onEvent={(event, error) => {
        console.log("onKeycloakEvent", event, error);
      }}
    ></ReactKeycloakProvider>
  ) : null;
};
class StandardLoginForm extends React.Component {
  constructor(props) {
    super(props);
    this.core = this.props.core;
    this.state = {
      showPage: "login",
      error: false,
      username: "",
      password: "",
      resetPasswordToken: "",
      isLoading: false,
    };
    this.handleChange = this.handleChange.bind(this);
    this.loginAction = this.loginAction.bind(this);
  }


  async componentDidMount() {

    const translateLanguageOptions = this.core.config("translateLanguageOptions") || [];
    if (translateLanguageOptions.length > 0) {
      oxziongui.Utils.loadGoogleTranslate(
        translateLanguageOptions.join(','),
        'google_translate_element'
      );
    }

    const queryObj = window.location.search
      .substr(1)
      .split("&")
      .reduce((prev, curr) => {
        const p = curr.split("=");
        prev[decodeURIComponent(p[0])] = decodeURIComponent(p[1]);
        return prev;
      }, {});

    if (queryObj.resetpassword) {
      this.setState({
        showPage: "resetpassword",
        resetPasswordToken: queryObj.resetpassword,
      });
    }
  }

  handleChange(e) {
    let target = e.target;
    this.setState({ [target.name]: target.value }, () => {
      if (this.state.username.length >= 0 && this.state.password.length >= 0) {
        this.setState({ error: undefined });
      }
    });
  }

  loginAction(e) {
    e.preventDefault(); // Prevent form submission
    const { username, password } = this.state;
    if (username && password) {
      this.props.thisObj.emit("login:post", { username, password });
      this.props.thisObj.on("login:error", (error) => {
        console.log(error);
        this.setState({ error: "The username or password you entered is incorrect" });
      });
    } else {
      this.setState({ error: "Username or password cannot be empty." });
    }
  }

  render() {
    const { showPage } = this.state;
    switch (showPage) {
      case "forgotpassword":
        return <ForgotPassword core={this.core} showLoginPage={() => this.setState({ showPage: "login", error: false })} />;
      case "resetpassword":
        return (
          <ResetPasswordPage
            core={this.core}
            resetPasswordToken={this.state.resetPasswordToken}
            showLoginPage={() => this.setState({ showPage: "login" })}
          />
        );
      default:
        return <main id="login-container" className="loginContainer row lighten-3">
          <img
            src={this.core?.configuration?.siteSetting?.loginBackground ?? wallpaper}
            alt="EOX Vantage"
            style={{
              position: "absolute",
              width: "100vw",
              height: "135vh",
              top: "50%",
              left: "50%",
              transform: "translate(-50%,-50%)",
            }}
          />
          <div className="loginSlider col-8" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Slider core={this.core} />
          </div>
          <div id="ox-login-form" className="col-4">
            <div className="form-wrapper__inner" id="loginPage" style={{ display: "block", paddingTop: "10vh" }}>
              <form className="ox-form" method="post" onSubmit={this.loginAction}>
                <div id="ox-img" className="ox-imgDiv">
                  <img id="ox-logo" className="ox-img" src={this.core?.configuration?.siteSetting?.loginLogo ?? oxLogo} alt="Logo" />
                </div>
                <div className="floating-label">
                  <input
                    type="text"
                    name="username"
                    className="validate"
                    id="username"
                    placeholder="Username"
                    value={this.state.username}
                    onChange={this.handleChange}
                  />
                  <label htmlFor="username">Username</label>
                </div>
                <div className="floating-label">
                  <input
                    type="password"
                    name="password"
                    className="validate"
                    id="password"
                    placeholder="Password"
                    value={this.state.password}
                    onChange={this.handleChange}
                  />
                  <label htmlFor="password">Password</label>
                </div>
                <div className="form-signin__footer">
                  <button type="submit" value="login" className="btn waves-effect waves-light">
                    Login
                  </button>
                  <a onClick={() => this.setState({ showPage: "forgotpassword", error: null })}>Forgot your password?</a>
                </div>
              </form>

              {this.state.error && (
                <div className="osjs-login-error text-center">
                  <span>{this.state.error}</span>
                </div>
              )}
            </div>
            <footer className="footer-links">
              <div id="google_translate_element"></div>
              <a href="https://www.eoxvantage.com">About Us</a> &nbsp;&nbsp; | &nbsp;&nbsp;
              <a href="./privacy-policy" target="_blank">
                Privacy Policy
              </a>
              <div className="coppyRight">Copyright © {new Date().getFullYear()} EOX Vantage. All rights reserved.</div>
            </footer>
          </div>
        </main>;
    }
  }
}
